import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api";

const EMPTY_ADDRESS = {
  full_name: "",
  phone: "",
  house: "",
  area: "",
  landmark: "",
  pincode: "",
  town: "",
  state: "",
  is_default: false
};

export default function AddressForm({
  initialAddress = null,
  onSaved,
  onCancel,
  showCancel = false
}) {
  const isEditMode = Boolean(
    initialAddress?.address_id
  );

  const [addr, setAddr] = useState({
    ...EMPTY_ADDRESS,
    ...(initialAddress || {})
  });

  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState("");
  const [pinLocations, setPinLocations] = useState([]);

  // Keep the form synchronized when the parent switches
  // between adding and editing different addresses.
  useEffect(() => {
    setAddr({
      ...EMPTY_ADDRESS,
      ...(initialAddress || {})
    });

    setPinError("");
    setPinLocations([]);
  }, [initialAddress]);

  const lookupPincode = async (pincode) => {
    const pin = pincode
      .replace(/\D/g, "")
      .slice(0, 6);

    setAddr((a) => ({
      ...a,
      pincode: pin,
      town: pin.length < 6 ? "" : a.town,
      state: pin.length < 6 ? "" : a.state
    }));

    setPinError("");
    setPinLocations([]);

    if (pin.length !== 6) return;

    setPinLoading(true);

    try {
      const result = await apiFetch(
        `/address/pincode/${pin}`
      );

      const locations = result.locations || [];

      if (!locations.length) {
        throw new Error(
          "No locality found for this pincode"
        );
      }

      setAddr((a) => ({
        ...a,
        pincode: pin,
        state: result.state || "",
        town:
          locations.length === 1
            ? locations[0].name
            : ""
      }));

      setPinLocations(locations);
    } catch (err) {
      setPinError(
        err.message ||
          "Unable to find this pincode"
      );

      setAddr((a) => ({
        ...a,
        state: "",
        town: ""
      }));
    } finally {
      setPinLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (addr.phone.length !== 10) {
      return toast.error(
        "Enter a valid 10-digit mobile number"
      );
    }

    if (addr.pincode.length !== 6) {
      return toast.error(
        "Enter a valid 6-digit pincode"
      );
    }

    if (!addr.town) {
      return toast.error(
        "Please select or enter your town/city"
      );
    }

    if (!addr.state) {
      return toast.error(
        "Please enter a valid pincode first"
      );
    }

    try {
      const endpoint = isEditMode
        ? `/addresses/${initialAddress.address_id}`
        : "/addresses";

      const method = isEditMode
        ? "PUT"
        : "POST";

      const saved = await apiFetch(
        endpoint,
        {
          method,
          body: JSON.stringify(addr)
        }
      );

      toast.success(
        isEditMode
          ? "Address updated successfully"
          : "Address saved successfully"
      );

      if (onSaved) {
        onSaved(saved);
      }
    } catch (err) {
      toast.error(
        err.message ||
          (
            isEditMode
              ? "Unable to update address"
              : "Unable to save address"
          )
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="addr-form"
    >
      <h3>
        {isEditMode
          ? "Edit address"
          : "Add new address"}
      </h3>

      <label>
        Full name
        <input
          value={addr.full_name}
          onChange={(e) =>
            setAddr({
              ...addr,
              full_name: e.target.value
            })
          }
          required
          data-testid="addr-full_name"
        />
      </label>

      <label>
        Mobile number
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          value={addr.phone}
          onChange={(e) =>
            setAddr({
              ...addr,
              phone: e.target.value
                .replace(/\D/g, "")
                .slice(0, 10)
            })
          }
          required
          placeholder="10-digit mobile number"
          data-testid="addr-phone"
        />
      </label>

      <label>
        Flat, House no., Building, Company, Apartment
        <input
          value={addr.house}
          onChange={(e) =>
            setAddr({
              ...addr,
              house: e.target.value
            })
          }
          required
          data-testid="addr-house"
        />
      </label>

      <label>
        Area, Street, Sector, Village
        <input
          value={addr.area}
          onChange={(e) =>
            setAddr({
              ...addr,
              area: e.target.value
            })
          }
          required
          data-testid="addr-area"
        />
      </label>

      <label>
        Landmark
        <input
          value={addr.landmark}
          onChange={(e) =>
            setAddr({
              ...addr,
              landmark: e.target.value
            })
          }
          placeholder="Optional"
          data-testid="addr-landmark"
        />
      </label>

      <label>
        Pincode
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={addr.pincode}
          onChange={(e) =>
            lookupPincode(e.target.value)
          }
          required
          placeholder="6-digit pincode"
          data-testid="addr-pincode"
        />
      </label>

      {pinLoading && (
        <p className="addr-pin-status">
          Checking pincode...
        </p>
      )}

      {pinError && (
        <p className="addr-pin-error">
          {pinError}
        </p>
      )}

      {pinLocations.length > 1 && (
        <label>
          Select locality / post office

          <select
            value={addr.town}
            onChange={(e) =>
              setAddr({
                ...addr,
                town: e.target.value
              })
            }
            required
            data-testid="addr-location"
          >
            <option value="">
              Select your locality
            </option>

            {pinLocations.map((location) => (
              <option
                key={location.name}
                value={location.name}
              >
                {location.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <label>
        Town/City
        <input
          value={addr.town}
          onChange={(e) =>
            setAddr({
              ...addr,
              town: e.target.value
            })
          }
          required
          placeholder="Town or city"
          data-testid="addr-town"
        />
      </label>

      <label>
        State
        <input
          value={addr.state}
          readOnly
          required
          data-testid="addr-state"
        />
      </label>

      <label className="addr-default-check">
        <input
          type="checkbox"
          checked={addr.is_default}
          onChange={(e) =>
            setAddr({
              ...addr,
              is_default: e.target.checked
            })
          }
          data-testid="addr-default"
        />

        <span>
          Make this my default address
        </span>
      </label>

      <div className="addr-form-actions">
        {showCancel && (
          <button
            type="button"
            className="button button-light"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}

        <button
          type="submit"
          className="button button-dark"
          disabled={pinLoading}
          data-testid="save-address"
        >
          {isEditMode
            ? "Update address"
            : "Save address"}

          <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}
